import { ColorEnum } from '@/constants/color'
import Button from '@/features/openTabs/components/AccountActionButton'
import AccountItemCard, { AccountItemView } from '@/features/openTabs/components/AccountItemCard'
import PrinterIcon from '@/features/ui/icons/components/PrinterIcon'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface OrderView {
    id: string
    title: string
    time: string
    totalPrice: string
    items: AccountItemView[]
}

interface OrderListProps {
    orders: OrderView[]
    onReprint?: (orderId: string) => void
    reprintDisabled?: boolean
    disabled?: boolean
    onChangeQuantity?: (itemId: string, change: -1 | 1) => void
    onRemove?: (itemId: string) => void
}

const OrderList: FC<OrderListProps> = ({
    orders,
    onReprint,
    reprintDisabled,
    disabled,
    onChangeQuantity,
    onRemove,
}) => {
    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet, {
        layout: layout === 'compact' ? 'compact' : undefined,
    })

    return (
        <View style={styles.container}>
            {orders.map((order) => (
                <View key={order.id} style={styles.order}>
                    <View style={styles.orderHeader}>
                        <View style={styles.orderInfo}>
                            <Typography.Description font="medium">
                                {order.title}
                                {layout !== 'compact' && ` · ${order.time}`}
                            </Typography.Description>
                            {layout === 'compact' && (
                                <Typography.Description style={styles.orderSummary}>
                                    {order.time} · {order.totalPrice}
                                </Typography.Description>
                            )}
                        </View>
                        {layout !== 'compact' && (
                            <>
                                <View style={styles.divider} />
                                <Typography.Description font="medium">
                                    {order.totalPrice}
                                </Typography.Description>
                            </>
                        )}
                        <Button
                            type="outline"
                            color="secondary"
                            size="medium"
                            FrontIcon={PrinterIcon}
                            disabled={!onReprint || reprintDisabled}
                            onPress={() => onReprint?.(order.id)}
                            style={styles.reprintButton}
                        >
                            {t('openTabs.actions.reprint')}
                        </Button>
                    </View>
                    {order.items.map((item) => (
                        <AccountItemCard
                            key={item.id}
                            item={item}
                            disabled={disabled}
                            onChangeQuantity={onChangeQuantity}
                            onRemove={onRemove}
                        />
                    ))}
                </View>
            ))}
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        width: '100%',
        gap: 16,
    },
    order: {
        width: '100%',
        gap: 8,
    },
    orderHeader: {
        minHeight: 32,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        variants: {
            layout: {
                compact: {
                    alignItems: 'flex-start',
                },
            },
        },
    },
    orderInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        variants: {
            layout: {
                compact: {
                    flex: 1,
                    minWidth: 0,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                },
            },
        },
    },
    orderSummary: {
        color: ColorEnum.GRAY_600,
    },
    divider: {
        flex: 1,
        height: 1,
        backgroundColor: ColorEnum.GRAY_200,
        variants: {
            layout: {
                compact: {
                    display: 'none',
                },
            },
        },
    },
    reprintButton: {
        borderRadius: 8,
    },
})

export default OrderList
